/**
 * C0-ROTATE: the painted ground turned (and pitched) on the GPU, with hard pixels on the art's grid.
 *
 * Each painted ground chunk the ground pass draws is put in the world picture as one quad, placed by
 * the turn's transform, with a shader that, for every pixel it covers, finds the middle of the art
 * pixel it falls in (the art grid: `res` world-picture pixels a side), maps four points round it
 * back onto the chunk's painted texels and keeps the commonest colour (the middle one breaking
 * ties). Every pixel is one of the art's colours, never a blend, the ground keeps the same pixel
 * size as the buildings and figures, and squeezed detail drops out by majority rather than on a
 * beat. Nothing is resampled on the CPU and nothing is uploaded again while the camera turns: the
 * chunks' textures are the same ones the unturned ground uses.
 *
 * A pixel whose art-pixel middle falls in a neighbouring chunk is left to that chunk (the quads
 * overlap by a few texels), so chunk edges show no seam.
 */
import { GpuProgram, Matrix, Mesh, MeshGeometry, Shader, UniformGroup, type Texture } from 'pixi.js';
import type { WebGLCanvas } from '../webgl';

/** How far (texels) each quad reaches past its chunk, for the art-pixel middles across the edge. */
const OVERLAP = 4;

const VERTEX = `
in vec2 aPosition;
in vec2 aUV;
out vec2 vScreen;
uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
void main() {
  vec3 w = uWorldTransformMatrix * uTransformMatrix * vec3(aPosition, 1.0);
  gl_Position = vec4((uProjectionMatrix * w).xy, 0.0, 1.0);
  vScreen = w.xy;
}`;

const FRAGMENT = `
in vec2 vScreen;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform vec4 uInvA;
uniform vec4 uInvB;
uniform vec4 uFrame;
uniform vec4 uSize;
vec2 back(vec2 s) {
  return vec2(uInvA.x * s.x + uInvA.z * s.y + uInvB.x, uInvA.y * s.x + uInvA.w * s.y + uInvB.y);
}
vec4 texel(vec2 l) {
  vec2 t = clamp(floor(l), vec2(0.0), uSize.xy - 1.0);
  vec2 k = uFrame.zw / uSize.xy;
  return texture(uTexture, (uFrame.xy + (t + 0.5) * k) / uSize.zw);
}
bool same(vec4 a, vec4 b) { return all(equal(a, b)); }
void main() {
  float r = uInvB.z;
  vec2 art = (floor(vScreen / r) + 0.5) * r;
  vec2 c = back(art);
  if (c.x < 0.0 || c.y < 0.0 || c.x >= uSize.x || c.y >= uSize.y) discard;
  vec2 dx = (back(art + vec2(r, 0.0)) - c) * 0.25;
  vec2 dy = (back(art + vec2(0.0, r)) - c) * 0.25;
  vec4 s1 = texel(c - dx - dy);
  vec4 s2 = texel(c + dx - dy);
  vec4 s3 = texel(c + dx + dy);
  vec4 s4 = texel(c - dx + dy);
  vec4 p;
  if (same(s1, s2) || same(s1, s3) || same(s1, s4)) p = s1;
  else if (same(s2, s3) || same(s2, s4)) p = s2;
  else if (same(s3, s4)) p = s3;
  else p = texel(c);
  finalColor = p;
}`;

const WGSL = /* wgsl */ `
struct GlobalUniforms {
  uProjectionMatrix: mat3x3<f32>,
  uWorldTransformMatrix: mat3x3<f32>,
  uWorldColorAlpha: vec4<f32>,
  uResolution: vec2<f32>,
}
struct LocalUniforms {
  uTransformMatrix: mat3x3<f32>,
  uColor: vec4<f32>,
  uRound: f32,
}
struct Gnd {
  uInvA: vec4<f32>,
  uInvB: vec4<f32>,
  uFrame: vec4<f32>,
  uSize: vec4<f32>,
}
@group(0) @binding(0) var<uniform> globalUniforms: GlobalUniforms;
@group(1) @binding(0) var<uniform> localUniforms: LocalUniforms;
@group(2) @binding(0) var<uniform> gnd: Gnd;
@group(2) @binding(1) var uTexture: texture_2d<f32>;
@group(2) @binding(2) var uSampler: sampler;

struct VOut {
  @builtin(position) position: vec4<f32>,
  @location(0) vScreen: vec2<f32>,
}

@vertex
fn mainVertex(@location(0) aPosition: vec2<f32>, @location(1) aUV: vec2<f32>) -> VOut {
  let w = globalUniforms.uWorldTransformMatrix * localUniforms.uTransformMatrix * vec3<f32>(aPosition, 1.0);
  var out: VOut;
  out.position = vec4<f32>((globalUniforms.uProjectionMatrix * w).xy, 0.0, 1.0);
  out.vScreen = w.xy;
  return out;
}

fn back(s: vec2<f32>) -> vec2<f32> {
  return vec2<f32>(gnd.uInvA.x * s.x + gnd.uInvA.z * s.y + gnd.uInvB.x, gnd.uInvA.y * s.x + gnd.uInvA.w * s.y + gnd.uInvB.y);
}
fn texel(l: vec2<f32>) -> vec4<f32> {
  let t = clamp(floor(l), vec2<f32>(0.0), gnd.uSize.xy - 1.0);
  let k = gnd.uFrame.zw / gnd.uSize.xy;
  return textureSampleLevel(uTexture, uSampler, (gnd.uFrame.xy + (t + 0.5) * k) / gnd.uSize.zw, 0.0);
}
fn same(a: vec4<f32>, b: vec4<f32>) -> bool { return all(a == b); }

@fragment
fn mainFragment(@location(0) vScreen: vec2<f32>) -> @location(0) vec4<f32> {
  let r = gnd.uInvB.z;
  let art = (floor(vScreen / r) + 0.5) * r;
  let c = back(art);
  if (c.x < 0.0 || c.y < 0.0 || c.x >= gnd.uSize.x || c.y >= gnd.uSize.y) { discard; }
  let dx = (back(art + vec2<f32>(r, 0.0)) - c) * 0.25;
  let dy = (back(art + vec2<f32>(0.0, r)) - c) * 0.25;
  let s1 = texel(c - dx - dy);
  let s2 = texel(c + dx - dy);
  let s3 = texel(c + dx + dy);
  let s4 = texel(c - dx + dy);
  var p = texel(c);
  if (same(s1, s2) || same(s1, s3) || same(s1, s4)) { p = s1; }
  else if (same(s2, s3) || same(s2, s4)) { p = s2; }
  else if (same(s3, s4)) { p = s3; }
  return p;
}`;

type Affine = readonly [number, number, number, number, number, number];
type Drawable = Parameters<WebGLCanvas['textureOf']>[0];

/** One recorded chunk draw: the image and where it goes (unturned art pixels, before the box offset). */
interface ChunkDraw {
  img: Drawable;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Slot {
  mesh: Mesh<MeshGeometry, Shader>;
  uniforms: UniformGroup;
  w: number;
  h: number;
}

const mul = (p: Affine, q: Affine): Affine => [
  p[0] * q[0] + p[2] * q[1],
  p[1] * q[0] + p[3] * q[1],
  p[0] * q[2] + p[2] * q[3],
  p[1] * q[2] + p[3] * q[3],
  p[0] * q[4] + p[2] * q[5] + p[4],
  p[1] * q[4] + p[3] * q[5] + p[5],
];

export class GroundGpu {
  private readonly draws: ChunkDraw[] = [];
  private readonly slots: Slot[] = [];
  /** A stand-in canvas context the ground pass draws its chunks into: they are only noted. */
  readonly recorder: CanvasRenderingContext2D;

  constructor() {
    const draws = this.draws;
    this.recorder = {
      drawImage(img: Drawable, x: number, y: number, w?: number, h?: number) {
        draws.push({ img, x, y, w: w ?? img.width, h: h ?? img.height });
      },
    } as unknown as CanvasRenderingContext2D;
  }

  /** Starts a frame's recording. */
  begin(): void {
    this.draws.length = 0;
  }

  /**
   * Puts the recorded chunks in the world picture at this point: `base` is the world picture's own
   * transform (art pixels → its pixels), `turn` the ground's turn and pitch, `x0`, `y0` the offset
   * the chunks were drawn at.
   */
  draw(world: WebGLCanvas, base: DOMMatrix, turn: Affine, x0: number, y0: number): void {
    const B: Affine = [base.a, base.b, base.c, base.d, base.e, base.f];
    // World-picture pixels a side of an art pixel (the art grid every pixel snaps to).
    const res = Math.hypot(base.a, base.b);
    const BT = mul(B, turn);
    for (let k = 0; k < this.draws.length; k++) {
      const dr = this.draws[k]!;
      const iw = dr.img.width;
      const ih = dr.img.height;
      if (!iw || !ih) continue;
      const tex = world.textureOf(dr.img);
      const T = mul(BT, [dr.w / iw, 0, 0, dr.h / ih, x0 + dr.x, y0 + dr.y]);
      const det = T[0] * T[3] - T[1] * T[2];
      if (!det) continue;
      // Inverse: world-picture pixel → chunk texel.
      const ia = T[3] / det;
      const ib = -T[1] / det;
      const ic = -T[2] / det;
      const id = T[0] / det;
      const ie = -(ia * T[4] + ic * T[5]);
      const iff = -(ib * T[4] + id * T[5]);
      const slot = this.slot(k, iw, ih, tex);
      const u = slot.uniforms.uniforms as Record<'uInvA' | 'uInvB' | 'uFrame' | 'uSize', Float32Array>;
      u.uInvA.set([ia, ib, ic, id]);
      u.uInvB.set([ie, iff, res, 0]);
      const f = tex.frame;
      u.uFrame.set([f.x, f.y, f.width, f.height]);
      u.uSize.set([iw, ih, tex.source.pixelWidth, tex.source.pixelHeight]);
      slot.uniforms.update();
      const sh = slot.mesh.shader!;
      if (sh.resources.uTexture !== tex.source) {
        sh.resources.uTexture = tex.source;
        sh.resources.uSampler = tex.source.style;
      }
      slot.mesh.texture = tex;
      slot.mesh.setFromMatrix(new Matrix(T[0], T[1], T[2], T[3], T[4], T[5]));
      world.node(slot.mesh);
    }
  }

  /** The `k`th quad, sized for a `w`×`h` chunk. */
  private slot(k: number, w: number, h: number, tex: Texture): Slot {
    let s = this.slots[k];
    if (!s) {
      const geometry = new MeshGeometry({
        positions: new Float32Array(8),
        uvs: new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]),
        indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
      });
      const uniforms = new UniformGroup({
        uInvA: { value: new Float32Array(4), type: 'vec4<f32>' },
        uInvB: { value: new Float32Array(4), type: 'vec4<f32>' },
        uFrame: { value: new Float32Array(4), type: 'vec4<f32>' },
        uSize: { value: new Float32Array(4), type: 'vec4<f32>' },
      });
      const shader = Shader.from({
        gl: { vertex: VERTEX, fragment: FRAGMENT, name: 'turned-ground' },
        gpu: GpuProgram.from({
          vertex: { source: WGSL, entryPoint: 'mainVertex' },
          fragment: { source: WGSL, entryPoint: 'mainFragment' },
          name: 'turned-ground',
        }),
        resources: { uTexture: tex.source, uSampler: tex.source.style, gnd: uniforms },
      });
      s = this.slots[k] = { mesh: new Mesh<MeshGeometry, Shader>({ geometry, shader, texture: tex }), uniforms, w: 0, h: 0 };
    }
    if (s.w !== w || s.h !== h) {
      s.w = w;
      s.h = h;
      const e = OVERLAP;
      s.mesh.geometry.positions = new Float32Array([-e, -e, w + e, -e, w + e, h + e, -e, h + e]);
    }
    return s;
  }
}
